import {QuestionsAPI} from 'api/questions';
import {TrackingAPI} from 'api/tracking';
import {AcceptanceRate} from 'components/questions/acceptance_rate';
import {AsyncButton} from 'components/system/async_button';
import {DifficultyLevel} from 'components/system/difficulty_level';
import {PillTag} from 'components/system/pilltag';
import {PillTagGroup} from 'components/system/pilltag_group';
import ToolTip from 'components/system/tooltip';
import {useIsMobile} from 'hooks/is_mobile';
import {CheckmarkIcon, StarIcon} from 'icons';
import {LockIcon} from 'icons/lock';
import Link from 'next/link';
import {useRef} from 'react';
import {useAuth, useUpsell} from 'users';
import {defaultQuestionListConfig} from 'utils/question_list';
import QuestionPreview from './question_preview';
import styles from './question_row.module.css';

export const QuestionRow = ({
  question,
  onQuestionChange,
  config,
  onConfigChange,
  onChangePreview,
  showPreview,
  ranking,
}: Props) => {
  const isMobile = useIsMobile();
  const {hasPermission} = useAuth();
  const {loginRequired} = useUpsell();
  const hoverState = useRef(false);

  const handleTagClick = (tag: string) => {
    if (config.tags.includes(tag)) {
      onConfigChange({
        ...config,
        tags: config.tags.filter((c) => c !== tag),
        page: 0,
      });
    } else {
      onConfigChange({
        ...config,
        tags: [...config.tags, tag],
        page: 0,
      });
    }
  };

  const handleCompanyClick = (company: string) => {
    if (config.companies.includes(company)) {
      onConfigChange({
        ...config,
        companies: config.companies.filter((c) => c !== company),
        page: 0,
      });
    } else {
      onConfigChange({
        ...config,
        companies: [...config.companies, company],
        page: 0,
      });
    }
  };

  const link = (
    <Link prefetch={false} href={`/questions/${question.slug}`}>
      <a
        className={styles.link}
        onMouseOver={() => {
          hoverState.current = true;
          setTimeout(() => {
            if (hoverState.current) {
              onChangePreview(question.slug);
            }
          }, 500);
        }}
        onMouseOut={() => {
          hoverState.current = false;
        }}
        onClick={(e) => {
          hoverState.current = false;
          // Only track recommendation clicked if using the default ordering which is
          // based on the recommender.
          if (config.ordering === defaultQuestionListConfig.ordering) {
            TrackingAPI.recommendedQuestionClicked({
              uuid: question.uuid,
              ranking,
              component: 'questionList',
            });
          }
        }}>
        {question.title}
      </a>
    </Link>
  );

  return (
    <tr className={styles.row}>
      <td>
        <div>
          {link}
          {showPreview && (
            <QuestionPreview
              question={question}
              onChange={(question_slug) => {
                onChangePreview(question_slug);
              }}
            />
          )}
        </div>
      </td>
      <td className={styles.score}>{question.upvotes < 0 ? 0 : question.upvotes}</td>
      <td>
        <PillTagGroup maxVisible={isMobile ? 1 : 2}>
          {question.tags
            .sort((a, b) => {
              //sort first buy primary
              return a.is_primary && !b.is_primary ? -1 : 1;
            })
            .sort((a, b) => {
              //then by selected (more important)
              return config.tags?.includes(a.name) && !config.tags?.includes(b.name) ? -1 : 1;
            })
            .map((tag) => (
              <PillTag
                key={tag.name}
                color={config.tags?.includes(tag.name) ? 'blue' : 'greyLight'}
                onClick={() => handleTagClick(tag.name)}>
                {tag.name}
              </PillTag>
            ))}
        </PillTagGroup>
      </td>
      <td className={styles.companies}>
        {hasPermission('question_company_view') ? (
          <PillTagGroup maxVisible={isMobile ? 1 : 2}>
            {question.companies.map(({company}) => (
              <PillTag
                key={company}
                maxTextLength={isMobile ? 50 : 10}
                color={config.companies.includes(company) ? 'blue' : 'greyLight'}
                onClick={() => handleCompanyClick(company)}>
                {company}
              </PillTag>
            ))}
          </PillTagGroup>
        ) : (
          <span className={styles.hasIcon}>
            <LockIcon /> Locked
          </span>
        )}
      </td>
      <td>
        <DifficultyLevel level={question.level} />
      </td>
      <td>
        <AcceptanceRate value={question.acceptance_rate} />
      </td>
      <td>
        <div className={styles.actions}>
          <ToolTip tipText={question.saved ? 'Unsave question' : 'Save question'}>
            <AsyncButton
              title={question.saved ? 'Unsave question' : 'Save question'}
              name='saveQuestion'
              type='icon'
              allowClick={loginRequired({sourceObject: question, cta: 'question.save'})}
              onRequest={() => QuestionsAPI.saveQuestion(question.slug, !question.saved)}
              onSuccess={() =>
                onQuestionChange({
                  ...question,
                  saved: !question.saved,
                })
              }>
              <StarIcon color={question.saved ? 'orange' : 'greyLight'} />
            </AsyncButton>
          </ToolTip>
          <ToolTip tipText={question.completed ? 'Mark as uncompleted' : 'Mark as completed'}>
            <AsyncButton
              title={question.completed ? 'Mark as uncompleted' : 'Mark as completed'}
              name='markCompleted'
              type='icon'
              allowClick={loginRequired({sourceObject: question, cta: 'question.markCompleted'})}
              onRequest={() => QuestionsAPI.markCompleted(question.slug, !question.completed)}
              onSuccess={() =>
                onQuestionChange({
                  ...question,
                  completed: !question.completed,
                })
              }>
              <CheckmarkIcon color={question.completed ? 'blueLight' : 'greyLight'} />
            </AsyncButton>
          </ToolTip>
        </div>
      </td>
    </tr>
  );
};

type Props = {
  question: Question;
  onChangePreview: (questionSlug: string) => void;
  showPreview: boolean;
  onQuestionChange: (question: Question) => void;
  config: QuestionListConfig;
  onConfigChange: (config: QuestionListConfig) => void;
  ranking: number;
};
